import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as usersApi from '../api/usersManagement';

export function UsersManagement() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission('admin');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', name: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await usersApi.getUsers(selectedOrg.id);
      setUsers(result.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', role: 'viewer' });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, name: user.name, role: user.role, status: user.status });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrg || saving) return;
    
    setSaving(true);
    try {
      if (editingUser) {
        await usersApi.updateUser(selectedOrg.id, editingUser.id, {
          role: formData.role,
          status: formData.status,
          name: formData.name
        });
      } else {
        await usersApi.createUser(selectedOrg.id, {
          email: formData.email,
          name: formData.name,
          role: formData.role
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return { bg: '#e9d8fd', color: '#6b46c1' };
      case 'operator': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'viewer': return { bg: '#e2e8f0', color: '#4a5568' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: '#c6f6d5', color: '#276749' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'disabled': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need admin permissions to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Users & Roles"
        subtitle="Manage organization users and their roles"
      />

      <div style={styles.headerActions}>
        <button
          onClick={handleAddUser}
          style={styles.addButton}
          data-testid="add-user-button"
        >
          + Add User
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading users...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="users-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Last Login</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const roleStyle = getRoleColor(user.role);
                const statusStyle = getStatusColor(user.status);
                return (
                  <tr key={user.id} style={styles.tr} data-testid={`user-${user.id}`}>
                    <td style={styles.td}><strong>{user.name}</strong></td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: roleStyle.bg, color: roleStyle.color }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: statusStyle.bg, color: statusStyle.color }}>
                        {user.status}
                      </span>
                    </td>
                    <td style={styles.td}>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td style={styles.td}>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : '-'}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={styles.editButton}
                        data-testid={`edit-${user.id}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="user-modal">
            <h3 style={styles.modalTitle}>
              {editingUser ? 'Edit User' : 'Add User'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  style={styles.input}
                  disabled={!!editingUser}
                  required
                  data-testid="email-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  style={styles.input}
                  required
                  data-testid="name-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  style={styles.select}
                  data-testid="role-select"
                >
                  <option value="viewer">Viewer</option>
                  <option value="operator">Operator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {editingUser && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    style={styles.select}
                    data-testid="status-select"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              )}
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={styles.submitButton}
                  data-testid="submit-button"
                >
                  {saving ? 'Saving...' : (editingUser ? 'Update' : 'Add User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  headerActions: { marginBottom: '1rem' },
  addButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '400px', maxWidth: '500px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  formGroup: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' },
  select: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
